import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import QuickAnalyticsPanel from '../../components/analytics/QuickAnalyticsPanel';
import { financeService } from '../../services/financeService';

const FinanceDashboard = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [records, setRecords] = useState([]);
  const [regionProfiles, setRegionProfiles] = useState([]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [membershipRecords, profiles] = await Promise.all([
        financeService.getMembershipRecords(),
        financeService.getRegionFinanceProfiles(),
      ]);
      setRecords(membershipRecords);
      setRegionProfiles(profiles);
    } catch (err) {
      setError(err.message || 'Failed to load finance dashboard');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const summary = useMemo(
    () => financeService.computeSummary(records, regionProfiles),
    [records, regionProfiles]
  );

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-TZ', { style: 'currency', currency: 'TZS', maximumFractionDigits: 0 }).format(amount || 0);

  const stats = [
    { label: 'Total Records', value: summary.totalRecords, icon: 'Receipt', hint: 'All membership & license records' },
    { label: 'Paid', value: summary.paidCount, icon: 'CheckCircle', hint: 'Fully paid records', tone: 'success' },
    { label: 'Outstanding', value: summary.pendingCount, icon: 'Clock', hint: 'Pending, partial, or overdue', tone: summary.pendingCount ? 'warning' : 'neutral' },
    { label: 'Collected', value: formatCurrency(summary.totalCollected), icon: 'Banknote', hint: 'Total paid amount', tone: 'success' },
    { label: 'Outstanding Amount', value: formatCurrency(summary.totalOutstanding), icon: 'AlertTriangle', hint: 'Unpaid balance', tone: summary.totalOutstanding ? 'error' : 'neutral' },
    { label: 'Regions Configured', value: summary.regionsConfigured, icon: 'MapPin', hint: 'Regions with bank details on file' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'}`}>
        <div className="p-6 space-y-6 max-w-7xl mx-auto">
          <Breadcrumb items={[
            { label: 'Home', path: '/admin-dashboard' },
            { label: 'Finance Dashboard' },
          ]} />

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Wallet" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Finance Dashboard</h1>
                <p className="text-muted-foreground">
                  License memberships, payments, and regional finance overview
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" iconName="RefreshCw" onClick={loadData} disabled={isLoading}>
                Refresh
              </Button>
              <Button variant="outline" iconName="MapPin" asChild>
                <Link to="/region-finance">Region Profiles</Link>
              </Button>
              <Button iconName="Plus" asChild>
                <Link to="/membership-records">Manage Records</Link>
              </Button>
            </div>
          </div>

          {error && (
            <div className="p-4 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive text-sm flex items-center gap-2">
              <Icon name="AlertCircle" size={18} />
              {error}
            </div>
          )}

          <QuickAnalyticsPanel
            title="Finance Overview"
            description="Membership and license payment records for players, coaches, officials, and clubs."
            stats={stats}
            isLoading={isLoading}
            breakdownSections={[
              { title: 'Records by Entity Type', items: summary.byEntityType, chartType: 'donut' },
              { title: 'Records by Payment Status', items: summary.byStatus, chartType: 'donut' },
            ]}
          />

          {!isLoading && records.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-5 card-shadow">
              <h3 className="text-sm font-semibold text-foreground mb-4">Recent Membership Records</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-muted-foreground">
                      <th className="py-2 pr-4">Entity</th>
                      <th className="py-2 pr-4">Type</th>
                      <th className="py-2 pr-4">Amount</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2">Season</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.slice(0, 8).map((record) => (
                      <tr key={record.id} className="border-b border-border/60">
                        <td className="py-2 pr-4 font-medium">{record.entityName}</td>
                        <td className="py-2 pr-4 capitalize">{record.entityType?.replace('_', ' ')}</td>
                        <td className="py-2 pr-4">{formatCurrency(record.amount)}</td>
                        <td className="py-2 pr-4 capitalize">{record.paymentStatus?.replace('_', ' ')}</td>
                        <td className="py-2">{record.seasonYear || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default FinanceDashboard;
